import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({ baseURL: API });

http.interceptors.request.use((config) => {
  const token = localStorage.getItem("vg_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const company = localStorage.getItem("vg_company");
  if (company) config.headers["X-Company-Id"] = company;
  return config;
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function requestErrorMessage(error, action = "load this page") {
  const detail = error?.response?.data?.detail;
  if (detail != null) return formatApiError(detail);
  if (!error?.response) return `Could not ${action}. Check your connection and try again.`;
  return `Could not ${action} (HTTP ${error.response.status}). Please try again.`;
}

export const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const inrShort = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return "₹" + (v / 10000000).toFixed(2) + " Cr";
  if (v >= 100000) return "₹" + (v / 100000).toFixed(2) + " L";
  if (v >= 1000) return "₹" + (v / 1000).toFixed(1) + "K";
  return "₹" + v.toFixed(0);
};

// Share helpers — open the user's local WhatsApp / email client (no integration)
export const waLink = (phone, message) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const withCc = digits.length === 10 ? "91" + digits : digits;
  return `https://wa.me/${withCc}?text=${encodeURIComponent(message)}`;
};
export const mailtoLink = (email, subject, body) =>
  `mailto:${email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

// Public URL for a company's uploaded logo (works in-app and in emailed/public views)
export const companyLogoUrl = (c) => {
  if (!c || !c.has_logo) return null;
  const cid = c.company_id || localStorage.getItem("vg_company") || "default";
  return `${API}/public/company/${cid}/logo`;
};

// Private image for staff; share-token-scoped image for public invoice recipients.
export const companySignatureUrl = (c, cacheBust) => {
  if (!c || !c.has_signature) return null;
  const url = c.signature_invoice_token
    ? `${API}/public/invoice/${encodeURIComponent(c.signature_invoice_token)}/signature`
    : `${API}/company/${encodeURIComponent(c.company_id || localStorage.getItem("vg_company") || "default")}/signature`;
  return cacheBust ? `${url}?t=${encodeURIComponent(cacheBust)}` : url;
};

// Client-side mirror of backend GST computation for live preview
export function computeTotals(items, posCode, opts = {}) {
  const companyCode = "36";
  const intra = String(posCode) === companyCode;
  let subtotal = 0, totalTax = 0, cgst = 0, sgst = 0, igst = 0, totalDiscount = 0;
  const rows = items.map((it) => {
    const qty = Number(it.qty) || 0;
    const rate = Number(it.rate) || 0;
    const discPct = Number(it.discount_pct) || 0;
    const gstRate = Number(it.gst_rate) || 0;
    const gross = qty * rate;
    const discAmt = (gross * discPct) / 100;
    const taxable = gross - discAmt;
    const tax = (taxable * gstRate) / 100;
    subtotal += taxable;
    totalDiscount += discAmt;
    totalTax += tax;
    if (intra) { cgst += tax / 2; sgst += tax / 2; } else { igst += tax; }
    return { ...it, taxable, tax, amount: taxable + tax };
  });
  const shipping = Number(opts.shipping) || 0;
  const other = Number(opts.other_charges) || 0;
  const preRound = subtotal + totalTax + shipping + other;
  const tcs = (preRound * (Number(opts.tcs_pct) || 0)) / 100;
  const tds = (subtotal * (Number(opts.tds_pct) || 0)) / 100;
  const beforeRound = preRound + tcs - tds;
  let grand, roundOff;
  if (opts.round_off_enabled !== false) {
    grand = Math.round(beforeRound);
    roundOff = grand - beforeRound;
  } else {
    grand = beforeRound;
    roundOff = 0;
  }
  return { intra, rows, subtotal, totalDiscount, totalTax, cgst, sgst, igst,
    shipping, other, tcs, tds, roundOff, grand };
}

const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function twoDigit(n) {
  if (n < 20) return ones[n];
  return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
}
function threeDigit(n) {
  let str = "";
  if (n > 99) { str += ones[Math.floor(n / 100)] + " Hundred "; n %= 100; }
  if (n) str += twoDigit(n);
  return str.trim();
}
export function amountInWords(num) {
  num = Math.round(Number(num) || 0);
  if (num === 0) return "Zero Rupees Only";
  let str = "";
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  if (crore) str += threeDigit(crore) + " Crore ";
  if (lakh) str += threeDigit(lakh) + " Lakh ";
  if (thousand) str += threeDigit(thousand) + " Thousand ";
  if (num) str += threeDigit(num);
  return "Rupees " + str.trim() + " Only";
}
